import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../api/client';
import { buildDeviceHistoryPath } from './deviceHistoryPath';

/** Serial field that opens the device history page (submit on Enter / Поиск only). */
export default function FindDevicePage({ activeTab, onTabChange }) {
  const navigate = useNavigate();
  const [serialDraft, setSerialDraft] = useState('');
  const [lookupError, setLookupError] = useState(null);
  const [isLooking, setIsLooking] = useState(false);
  const lookupSeqRef = useRef(0);

  async function openSerial(raw) {
    const serial = String(raw || '').trim();
    if (!serial) {
      setLookupError('Введите серийный номер');
      return;
    }

    const seq = lookupSeqRef.current + 1;
    lookupSeqRef.current = seq;
    setLookupError(null);
    setIsLooking(true);

    try {
      const response = await apiFetch(`/api/devices/lookup?serial=${encodeURIComponent(serial)}`);
      const body = await response.json().catch(() => ({}));
      if (seq !== lookupSeqRef.current) {
        return;
      }
      if (!response.ok) {
        throw new Error(body.message || `Ошибка ${response.status}`);
      }

      const nextPath = buildDeviceHistoryPath(body.device_id, {
        serialNumber: body.serial_number || serial,
      });
      if (nextPath) {
        navigate(nextPath);
      }
    } catch (error) {
      if (seq === lookupSeqRef.current) {
        setLookupError(error.message || 'Не удалось найти устройство');
      }
    } finally {
      if (seq === lookupSeqRef.current) {
        setIsLooking(false);
      }
    }
  }

  return (
    <section className="space-y-4">
      <div className="max-w-xl space-y-2">
        <label htmlFor="find-device-serial" className="block text-sm font-medium text-slate-700 dark:text-slate-200">
          Серийный номер
        </label>
        <div className="flex flex-wrap items-stretch gap-2">
          <input
            id="find-device-serial"
            aria-label="Серийный номер"
            value={serialDraft}
            spellCheck={false}
            autoComplete="off"
            autoCapitalize="characters"
            placeholder="WFKA00000881"
            disabled={isLooking}
            onChange={(event) => {
              setLookupError(null);
              setSerialDraft(event.target.value.toUpperCase());
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                void openSerial(serialDraft);
              }
            }}
            className="min-h-10 min-w-0 flex-1 rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-900 shadow-sm outline-none focus:border-sky-500 dark:border-slate-700 dark:bg-slate-900 dark:text-white"
          />
          <button
            type="button"
            aria-label="Поиск"
            disabled={isLooking}
            onClick={() => void openSerial(serialDraft)}
            className="min-h-10 shrink-0 rounded-lg bg-sky-600 px-4 text-sm font-medium text-white transition hover:bg-sky-500 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isLooking ? 'Поиск…' : 'Поиск'}
          </button>
        </div>
        {lookupError ? <p className="text-sm text-red-600 dark:text-red-400">{lookupError}</p> : null}
        <p className="text-sm text-slate-500 dark:text-slate-400">
          Введите номер и нажмите Enter или «Поиск».
        </p>
      </div>
    </section>
  );
}
